import React from "react";
import { connect } from "react-redux";
import i18next from "i18next";
import { CircularProgress } from "@material-ui/core";
import { userService } from "../_services";
import { showGlobalModal } from "../_services/snackbarService";
import { userActions } from "../_actions";
import { serverUrl } from "../_constants";

const STATUS_LABELS = {
  generating: "ai_landing_status_generating",
  ready: "ai_landing_status_ready",
  fail: "ai_landing_status_fail",
};

const STATUS_ORDER = { ready: 1, generating: 2, fail: 3 };

function sortTemplates(templates) {
  return [...templates].sort((a, b) => {
    if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
    return (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
  });
}

class AILandingPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      templates: [],
      isLoading: false,
    };
  }

  componentDidMount() {
    this.loadTemplates();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.conferenceId !== this.props.conferenceId) {
      this.loadTemplates();
    }
  }

  generateTemplate = () => {
    const { conferenceId } = this.props;
    if (!conferenceId) return;

    userService.postWrapper(
      `api/ai/landing/generate/${conferenceId}`,
      {},
      () => {
        showGlobalModal(i18next.t("ai_landing_generating_started"));
        this.loadTemplates();
      },
      () => {}
    );
  };

  activateTemplate = (templateId) => {
    const { dispatch } = this.props;

    userService.postWrapper(
      `api/ai/landing/set-active/${templateId}`,
      {},
      () => this.loadTemplates(),
      (error) => {
        if (error) dispatch(userActions.set_error(error, "error"));
      }
    );
  };

  deactivateTemplate = (templateId) => {
    const { dispatch } = this.props;

    userService.postWrapper(
      `api/ai/landing/set-active/${templateId}?inactivate=1`,
      {},
      () => this.loadTemplates(),
      (error) => {
        if (error) dispatch(userActions.set_error(error, "error"));
      }
    );
  };

  loadTemplates = () => {
    const { conferenceId, dispatch } = this.props;
    if (!conferenceId) return;

    this.setState({ isLoading: true });

    userService.getWrapper(
      `api/ai/landing/list/${conferenceId}`,
      (data) => {
        this.setState({
          templates: data.data || [],
          isLoading: false,
        });
      },
      (error) => {
        this.setState({ isLoading: false });
        if (error) dispatch(userActions.set_error(error, "error"));
      }
    );
  };

  render() {
    const { templates, isLoading } = this.state;

    return (
      <div style={{ marginLeft: 30, marginRight: 30, marginTop: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 16 }}>
          <h2 className="layout__block-title" style={{ margin: 0 }}>
            {i18next.t("ai_landing_templates")}
          </h2>
          <button
            className="btn"
            type="button"
            style={{ width: "auto" }}
            onClick={this.generateTemplate}
          >
            {i18next.t("ai_landing_generate")}
          </button>
        </div>

        {isLoading && (
          <div className="shadow">
            <div className="shadow_circle">
              <CircularProgress />
            </div>
          </div>
        )}

        {!isLoading && !templates.some((t) => !!t.is_active) && (
          <div style={{
            background: "#fff",
            border: "1px solid #e0e0e0",
            borderLeft: "4px solid #a9bbcb",
            borderRadius: 6,
            padding: "14px 20px",
            marginBottom: 16,
            color: "#5a7a95",
            fontSize: 15,
            fontWeight: 500,
          }}>
            {i18next.t("ai_landing_default_used")}
          </div>
        )}

        {!isLoading && templates.length === 0 && (
          <p style={{ color: "#a9bbcb", marginTop: 20 }}>
            {i18next.t("ai_landing_no_templates")}
          </p>
        )}

        {!isLoading && templates.length > 0 && (
          <div className="tbl">
            <div className="tbl__top">
              <div className="tbl__items">
                <div className="tbl__item" style={{ width: 80 }}>
                  <strong>{i18next.t("ai_landing_col_id")}</strong>
                </div>
                <div className="tbl__item" style={{ flex: 1 }}>
                  <strong>{i18next.t("ai_landing_col_status")}</strong>
                </div>
                <div className="tbl__item" style={{ width: 120 }}>
                  <strong>{i18next.t("ai_landing_col_active")}</strong>
                </div>
                <div className="tbl__item" style={{ width: 220 }}>
                  <strong>{i18next.t("ai_landing_col_actions")}</strong>
                </div>
              </div>
            </div>

            <div className="tbl__body">
              {sortTemplates(templates).map((item) => (
                <div
                  key={item.id}
                  className="tbl__items"
                  style={item.is_active ? { backgroundColor: "#e6f4ea" } : {}}
                >
                  <div className="tbl__item" style={{ width: 80 }}>
                    {item.id}
                  </div>
                  <div className="tbl__item" style={{ flex: 1 }}>
                    {i18next.t(STATUS_LABELS[item.status] || item.status)}
                  </div>
                  <div className="tbl__item" style={{ width: 120 }}>
                    {item.is_active
                      ? i18next.t("ai_landing_yes")
                      : i18next.t("ai_landing_no")}
                  </div>
                  <div className="tbl__item" style={{ width: 220, display: "flex", gap: 8 }}>
                    {item.status === "ready" && (
                      <a
                        href={`${serverUrl}ai/landing/${item.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn--border"
                      >
                        {i18next.t("ai_landing_preview")}
                      </a>
                    )}
                    {item.status === "ready" && !item.is_active && (
                      <button
                        className="btn btn--border"
                        type="button"
                        onClick={() => this.activateTemplate(item.id)}
                      >
                        {i18next.t("ai_landing_activate")}
                      </button>
                    )}
                    {!!item.is_active && (
                      <button
                        className="btn btn--border"
                        type="button"
                        onClick={() => this.deactivateTemplate(item.id)}
                      >
                        {i18next.t("ai_landing_deactivate")}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { app, user } = state;
  return { app, user };
}

const connectedAILandingPage = connect(mapStateToProps)(AILandingPage);
export { connectedAILandingPage as AILandingPage };
